import { MessageSquare, BookOpen, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { JurisdictionSelect } from "@/components/form/JurisdictionSelect";
import { UserRoleSelect } from "@/components/form/UserRoleSelect";
import { InstructionWordLimit } from "@/components/form/InstructionWordLimit";
import { Textarea } from "@/components/ui/textarea";

interface Step2ConfigProps {
  jurisdiction: string;
  onJurisdictionChange: (value: string) => void;
  userRole: string;
  onUserRoleChange: (value: string) => void;
  agentInstructions: string;
  onAgentInstructionsChange: (value: string) => void;
  skipResearch: boolean;
  onSkipResearchChange: (value: boolean) => void;
}

export function Step2Config({
  jurisdiction,
  onJurisdictionChange,
  userRole,
  onUserRoleChange,
  agentInstructions,
  onAgentInstructionsChange,
  skipResearch,
  onSkipResearchChange,
}: Step2ConfigProps) {
  return (
    <div className="space-y-6">
      {/* Jurisdiction Section */}
      <div className="space-y-2">
        <JurisdictionSelect
          value={jurisdiction}
          onChange={onJurisdictionChange}
        />
        <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
          The jurisdiction determines which legal framework applies to this contract review.
        </p>
      </div>

      {/* User Role Section */}
      <div className="space-y-2">
        <UserRoleSelect
          value={userRole}
          onChange={onUserRoleChange}
        />
        <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
          Select the perspective from which the contract should be analyzed.
        </p>
      </div>

      {/* Agent Instructions */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <label className="block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
            Special Instructions
            <span className="ml-1 text-xs font-normal text-[var(--lm-muted)] dark:text-slate-500">(Optional)</span>
          </label>
        </div>
        <Textarea
          value={agentInstructions}
          onChange={(e) => onAgentInstructionsChange(e.target.value)}
          placeholder="Add any specific instructions for the AI reviewer (e.g., focus on termination clauses, check for liability caps, analyze IP ownership)..."
          rows={4}
          className="resize-none"
        />
        <InstructionWordLimit
          text={agentInstructions}
          kind="review-additional"
          onSummarized={onAgentInstructionsChange}
        />
      </div>

      {/* Skip Research Toggle */}
      <div className="rounded-xl border border-[var(--outline-border)] bg-[var(--surface-sunken)] p-4 dark:border-slate-700 dark:bg-slate-900/30">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--tint-amber-bg)] text-[#b54708] dark:bg-amber-500/10 dark:text-amber-400">
            <BookOpen className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
                  Skip Legal Research
                </p>
                <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
                  Disable precedent research for faster analysis
                </p>
              </div>
              <button
                onClick={() => onSkipResearchChange(!skipResearch)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[var(--surface-1)] dark:focus:ring-offset-slate-900",
                  skipResearch ? "bg-primary" : "bg-[var(--tab-segment-bg)] dark:bg-slate-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    skipResearch ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>

            {skipResearch && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-[rgba(234,179,8,0.35)] bg-[var(--tint-amber-bg)] p-3 dark:border-amber-500/25 dark:bg-amber-500/10">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#b54708] dark:text-amber-400" />
                <p className="text-xs text-[#b54708] dark:text-amber-200/90">
                  Research will be skipped. The review will be faster but may not include relevant legal precedents or case law.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
